import { Component, inject, OnInit, effect, ChangeDetectorRef } from '@angular/core';
import { SITE_DATA } from '../productization/default.constant';
import { SignupQuestionsSignal } from '../signal/signup-questions.signal';
import { Store } from '@ngxs/store';
import { GenericActions } from '../store/generic.actions';
import {
	ISignupQuestions
} from '../models/signup-questions.model';
import * as _ from 'underscore';
import { Router } from '@angular/router';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { FirstStepComponent } from './first-step/first-step.component';
import { SecondStepComponent } from './second-step/second-step.component';
import { ThirdStepComponent } from './third-step/third-step.component';
import { FourthStepComponent } from './fourth-step/fourth-step.component';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';

@Component({
	selector: 'app-signup-questions',
	templateUrl: './signup-questions.component.html',
	styleUrl: './signup-questions.component.scss',
	imports: [NzStepsModule, FirstStepComponent, SecondStepComponent, ThirdStepComponent, FourthStepComponent, NzSpaceModule, NzButtonModule, NzModalModule]
})
export class SignupQuestionsComponent implements OnInit {
	private readonly store = inject(Store);
	private readonly router = inject(Router);
	private readonly signupQuestionsSignal = inject(SignupQuestionsSignal);
	private readonly cdr = inject(ChangeDetectorRef);

	public siteData = SITE_DATA;
	public currentStep = 0;
	public isButtonEnabled = false;
	public stateData: ISignupQuestions;
	public isFlexibleSalaryModalVisible = false;

	constructor() {
		// Effect to watch state from signal
		effect(() => {
			const signUpQuestions = this.signupQuestionsSignal.state();
			
			if (signUpQuestions.isProfileDetailsCompleted) {
				this.router.navigate(['/candidate-profile']);
			}

			this.currentStep = signUpQuestions.step;
			this.stateData = signUpQuestions;
			this.isButtonEnabled = false;

			// Step 0: Your roles and experience (rolesExperience)
			if (!_.isEmpty(signUpQuestions.rolesExperience) && this.currentStep === 0) {
				const rolesExp = this.stateData.rolesExperience;
				const hasRequiredFields = !!(rolesExp.jobCategoryId && rolesExp.specializations?.length > 0 && rolesExp.yearsOfExperience !== null && rolesExp.yearsOfExperience !== undefined);
				const hasJobProfiles = (rolesExp.jobProfiles?.length ?? 0) > 0;
				this.isButtonEnabled = hasRequiredFields && hasJobProfiles;
			}

			// Step 1: Salary and Work preferences (salaryWorkPreference)
			if (!_.isEmpty(signUpQuestions.salaryWorkPreference) && this.currentStep === 1) {
				const salaryPref = this.stateData.salaryWorkPreference;
				const hasRequiredFields = !!(salaryPref.preferredWorkLocationId && salaryPref.workAvailabilityId);
				this.isButtonEnabled = hasRequiredFields;
			}

			// Step 2: Get to know you (backgroundDetails)
			if (!_.isEmpty(signUpQuestions.backgroundDetails) && this.currentStep === 2) {
				const bgDetails = this.stateData.backgroundDetails;
				// No required fields - only validate linkedIn URL pattern if provided
				const linkedInPattern = /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[a-zA-Z0-9_-]+\/?$/;
				const isLinkedInValid = !bgDetails.linkedInUrl || linkedInPattern.test(bgDetails.linkedInUrl);
				this.isButtonEnabled = isLinkedInValid;
			}

			// Trigger change detection to update the stepper
			this.cdr.detectChanges();
		});
	}

	ngOnInit(): void {
		this.store.dispatch([new GenericActions.GetGeoCountries()]);
		this.store.dispatch([new GenericActions.GetJobProfileCategories()]);
		localStorage.setItem('onboarding-current-step', 'current-role');
		
		// Get initial data from API
		this.signupQuestionsSignal.getSignupQuestions();
	}

	public pre(): void {
		const nextStep = this.currentStep - 1;
		this.signupQuestionsSignal.moveToStep(nextStep);
		this.setLocalStorageForCurrentStep(nextStep);
	}

	public next(): void {
		this.isButtonEnabled = false;

		if (this.currentStep === 0) {
			this.saveStepOne();
		}

		if (this.currentStep === 1) {
			// Check if flexible salary is not selected, show modal
			if (!this.stateData.salaryWorkPreference?.isSalaryNegotiable) {
				this.isFlexibleSalaryModalVisible = true;
				return;
			}
			this.saveSalaryPreferences();
		}

		if (this.currentStep === 2) {
			this.saveBackgroundDetails();
		}

		const nextStep = this.currentStep + 1;
		this.signupQuestionsSignal.moveToStep(nextStep);
		this.setLocalStorageForCurrentStep(nextStep);
	}

	public onFlexibleSalaryModalCancel(): void {
		this.isFlexibleSalaryModalVisible = false;
	}

	public onFlexibleSalaryModalContinue(): void {
		this.isFlexibleSalaryModalVisible = false;
		this.saveSalaryPreferences();
		const nextStep = this.currentStep + 1;
		this.signupQuestionsSignal.moveToStep(nextStep);
		this.setLocalStorageForCurrentStep(nextStep);
	}

	public done(): void {
		const exp = [
			{ label: '0-1 year', value: 0 },
			{ label: '1-2 years', value: 1 },
			{ label: '2-3 years', value: 2 },
			{ label: '3-4 years', value: 3 },
			{ label: '5-10 years', value: 5 },
			{ label: '11 and up', value: 11 }
		];

		const experience = exp.find(
			(item) => item.value === this.stateData.rolesExperience.yearsOfExperience
		);

		this.router.navigate(['/job-list'], {
			queryParams: {
				category: this.stateData.rolesExperience?.jobCategoryLabel ?? '',
				experience: experience?.label ?? ''
			}
		});
	}

	public skip(): void {
		const nextStep = this.currentStep + 1;
		this.signupQuestionsSignal.moveToStep(nextStep);
		this.setLocalStorageForCurrentStep(nextStep);
	}

	public cancel(): void {
		this.router.navigate(['/']);
	}

	private saveStepOne(): void {
		this.signupQuestionsSignal.saveStepOne();
	}

	private saveSalaryPreferences(): void {
		this.signupQuestionsSignal.saveSalaryPreferences();
	}

	private saveBackgroundDetails(): void {
		this.signupQuestionsSignal.saveBackgroundDetails();
	}

	private setLocalStorageForCurrentStep(step: number): void {
		switch (step) {
			case 0:
				localStorage.setItem('onboarding-current-step', 'current-role');
				break;
			case 1:
				localStorage.setItem('onboarding-current-step', 'interest-work-setup');
				break;
			case 2:
				localStorage.setItem('onboarding-current-step', 'desired-salary');
				break;
			case 3:
				localStorage.setItem('onboarding-current-step', 'review');
				break;
		}
	}
}